if (!customElements.get('gallery-zoom-open')) {
  class GalleryZoomOpen extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick.bind(this));
    }

    /**
     * Handle a click event.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const mediaGallery = this.closest('media-gallery');
      const zoomDevice = mediaGallery.dataset.zoomEnabled;

      if (zoomDevice.length === 0 || (zoomDevice === 'mobile' && !theme.mediaMatches.md) || (zoomDevice === 'desktop' && theme.mediaMatches.md)) {
        const zoom = mediaGallery.galleryModal.querySelector('gallery-zoom');
        // Hand the zoom its owning gallery BEFORE inserting the modal: each
        // <media-gallery> clones its own modal, but outside the product page
        // (featured-product on article/home templates) the .cc-main-product
        // fallback in connectedCallback finds nothing, and connectedCallback
        // (which builds the zoom thumbs from this gallery's DOM) fires as
        // soon as the modal is appended below.
        zoom.mediaGallery = mediaGallery;

        // Add to document on first open
        if (!mediaGallery.galleryModal.parentElement) {
          document.body.appendChild(mediaGallery.galleryModal);
        }

        mediaGallery.galleryModal.open(evt.currentTarget);
        zoom.init(evt.currentTarget.parentElement.dataset.mediaId);
        zoom.focus();

        evt.preventDefault();
      }
    }
  }

  window.customElements.define('gallery-zoom-open', GalleryZoomOpen);
}

/* eslint-disable max-len */

if (!customElements.get('gallery-zoom')) {
  class GalleryZoom extends HTMLElement {
    connectedCallback() {
      if (!this.initialised) {
        this.initialised = true;

        // ui
        this.classList.add('gallery-zoom--pre-reveal');
        this.zoomContainer = this.querySelector('.gallery-zoom__zoom-container');
        this.thumbContainer = this.querySelector('.gallery-zoom__thumbs');
        this.controlsContainer = this.querySelector('.gallery-zoom__controls');
        this.previousBtn = this.querySelector('.gallery-zoom__prev');
        this.nextBtn = this.querySelector('.gallery-zoom__next');
        this.counterCurrent = this.querySelector('.media-ctrl__current-item');
        this.counterTotal = this.querySelector('.media-ctrl__total-items');
        // Owning gallery is assigned by <gallery-zoom-open> on each open; the
        // selector is only a fallback for the product page.
        this.mediaGallery = this.mediaGallery || document.querySelector('.cc-main-product media-gallery');
        this.thumbSlider = this.querySelector('.gallery-zoom__thumb-slider');

        // consts
        this.wheelZoomMultiplier = -0.001;
        this.pinchZoomMultiplier = 0.003;
        this.touchPanModifier = 1.0;

        // vars
        this.currentZoomImage = null;
        this.currentTransform = {
          panX: 0,
          panY: 0,
          zoom: 1
        };
        this.pinchTracking = {
          isTracking: false,
          lastPinchDistance: 0
        };
        this.touchTracking = {
          isTracking: false,
          lastTouchX: 0,
          lastTouchY: 0
        };

        // events
        this.buildThumbs();
        this.addEventListener('touchend', this.stopTrackingTouch.bind(this));
        this.addEventListener('touchmove', this.trackInputMovement.bind(this));
        this.addEventListener('mousemove', this.trackInputMovement.bind(this));
        this.addEventListener('wheel', this.trackWheel.bind(this));
        // prevent pan while swiping thumbnails
        this.thumbContainer.addEventListener('touchmove', (evt) => evt.stopPropagation());
        this.previousBtn.addEventListener('click', this.selectPreviousThumb.bind(this));
        this.nextBtn.addEventListener('click', this.selectNextThumb.bind(this));
        this.zoomContainer.addEventListener('click', this.onZoomContainerClick.bind(this));
        new ResizeObserver(() => this.setInitialImagePosition()).observe(this);
      }

      document.documentElement.classList.add('gallery-zoom-open');
      this.addEventListener('keyup', this.handleKeyup.bind(this));
      setTimeout(() => this.classList.remove('gallery-zoom--pre-reveal'), 10);
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      document.documentElement.classList.remove('gallery-zoom-open');
    }

    /**
     * Helper for creating a DOM element.
     * @param {string} type - Element type.
     * @param {string} className - Class to apply to element.
     * @param {Element} appendTo - Element to add to as a child.
     * @param {string} innerHTML - Inner HTML to apply to element.
     * @returns {Element} - The element created.
     */
    static createEl(type, className, appendTo, innerHTML) {
      const el = document.createElement(type);
      el.className = className;
      if (appendTo) {
        appendTo.insertAdjacentElement('beforeend', el);
      }
      if (innerHTML) {
        el.innerHTML = innerHTML;
      }
      return el;
    }

    /**
     * Builds the zoom-modal thumb <li> items from the owning media-gallery's
     * viewer DOM instead of a second server-rendered image pass — every zoom
     * thumb needs is already on the matching .media-viewer__item (its zoom
     * link href is the exact zoom-resolution URL, its image the source to
     * downsize for the thumb). Visibility/active state isn't set here: init()
     * (called on every modal open) already re-derives thumb.hidden from the
     * live gallery state, and selectThumb() sets the active one.
     *
     * Incremental, not build-once: media-gallery.js can merge more color
     * groups into the viewer well after this element first connects
     * (background hydration — see warmVariantAssetGroups/
     * hydrateVariantAssetGroup), including while this modal is already open,
     * so this only adds thumbs for viewer items that don't have one yet
     * instead of bailing out once any thumb exists. Called again from
     * init() (every modal open) and directly by hydrateVariantAssetGroup.
     *
     * Walks the viewer in order and places each thumb right after the one
     * before it (existing or just-built), rather than always appending new
     * ones at the end — a color hydrated after the initial build otherwise
     * lands after every native-media thumb instead of leading over them,
     * same order the viewer itself keeps. A newly-built thumb also copies
     * its viewer item's current display state, since a group hydrated while
     * this modal is already open needs its thumb hidden immediately if it
     * isn't the active color, not just whenever the next init() happens.
     */
    buildThumbs() {
      if (!this.thumbContainer || !this.mediaGallery) return;

      const srcWidth = Number(this.thumbContainer.dataset.thumbSrcWidth) || 80;
      const imgClass = this.thumbContainer.dataset.thumbImgClass || '';
      const paddingTop = this.thumbContainer.dataset.thumbPaddingTop;
      const btnClass = this.thumbContainer.dataset.thumbBtnClass || 'media-thumbs__btn';

      let previousThumb = null;
      this.mediaGallery.querySelectorAll('.media-viewer__item[data-media-type="image"]').forEach((viewerItem) => {
        let li = this.thumbContainer.querySelector(`[data-media-id="${viewerItem.dataset.mediaId}"]`);

        if (!li) {
          const zoomLink = viewerItem.querySelector('.js-zoom-link');
          const sourceImg = viewerItem.querySelector('img.product-image');
          if (!zoomLink || !sourceImg) return;

          li = document.createElement('li');
          li.className = 'gallery-zoom__thumb slider__item flex';
          li.dataset.zoomUrl = zoomLink.href;
          li.dataset.mediaId = viewerItem.dataset.mediaId;
          li.hidden = viewerItem.style.display === 'none';

          const btn = document.createElement('button');
          btn.className = btnClass;
          if (paddingTop) btn.style.paddingTop = paddingTop;

          const img = document.createElement('img');
          img.className = imgClass;
          // Packshot spacing (ClickUp 869f1nrd3): same copy-the-marker rule as
          // media-gallery.js buildThumbItem — `.img--packshot` is resolved in
          // Liquid on the viewer image, and these thumbs are built from it.
          if (sourceImg.classList.contains('img--packshot')) {
            img.classList.add('img--packshot');
          }
          img.loading = 'lazy';
          img.alt = sourceImg.alt;
          img.src = GalleryZoom.resizeImageUrl(sourceImg.src, srcWidth);
          img.srcset = `${img.src}, ${GalleryZoom.resizeImageUrl(sourceImg.src, srcWidth * 2)} 2x`;
          btn.appendChild(img);

          li.appendChild(btn);
          li.addEventListener('click', this.onThumbClick.bind(this));

          if (previousThumb) {
            previousThumb.after(li);
          } else {
            this.thumbContainer.prepend(li);
          }
        }

        previousThumb = li;
      });
    }

    /**
     * Swaps (or appends) the Shopify CDN 'width' query param on an image URL.
     * @param {string} url - Source CDN image URL.
     * @param {number} width - Desired width.
     * @returns {string} - Resized image URL.
     */
    static resizeImageUrl(url, width) {
      const w = Math.round(width);
      if (/[?&]width=\d+/.test(url)) {
        return url.replace(/([?&])width=\d+/, `$1width=${w}`);
      }
      return `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
    }

    init(currentMediaId) {
      // Catches any color group background-hydrated since this element last
      // built thumbs (including while this modal was closed) — see the
      // comment on buildThumbs().
      this.buildThumbs();

      // Toggle thumbnail visibility based on the current state of media
      // grouping and/or variant metafield assets (both mirror the display
      // state of the matching gallery item, so one sync covers both).
      if (this.mediaGallery.dataset.mediaGroupingEnabled || this.mediaGallery.dataset.variantAssetsEnabled) {
        let visibleThumbCount = 0;
        Array.from(this.thumbContainer.children).forEach((thumb) => {
          const mediaGalleryImage = this.mediaGallery.querySelector(`.media-viewer__item[data-media-id="${thumb.dataset.mediaId}"]`);
          thumb.hidden = mediaGalleryImage?.style.display === 'none';
          if (!thumb.hidden) visibleThumbCount += 1;
        });
        if (this.counterTotal) this.counterTotal.textContent = visibleThumbCount;

        // The "single image" state (no thumb strip, no prev/next) is
        // server-rendered from the product's full image count, which cannot
        // see what a given variant actually shows — assets-only mode reduces
        // it to one slide for any variant whose metafield holds a single
        // image. Recompute it from the visible set on every open.
        const isSingle = visibleThumbCount <= 1;
        this.classList.toggle('gallery-zoom--single-image', isSingle);
        this.thumbContainer.hidden = isSingle;
        this.previousBtn.hidden = isSingle;
        this.nextBtn.hidden = isSingle;
      }

      this.selectThumb(
        [...this.thumbContainer.children].find((el) => el.dataset.mediaId === currentMediaId)
         || this.thumbContainer.firstElementChild
      );
    }

    /**
     * Update pan values based on input event coordinates.
     * @param {number} inputX - Mouse/touch input X.
     * @param {number} inputY - Mouse/touch input Y.
     */
    panZoomImageFromCoordinate(inputX, inputY) {
      // do nothing if the image fits, pan if not
      const doPanX = this.currentZoomImage.clientWidth > this.zoomContainer.clientWidth;
      const doPanY = this.currentZoomImage.clientHeight > this.zoomContainer.clientHeight;

      if (doPanX || doPanY) {
        const midX = this.zoomContainer.clientWidth / 2;
        const midY = this.zoomContainer.clientHeight / 2;

        const offsetFromCentreX = inputX - midX;
        const offsetFromCentreY = inputY - midY;

        // the offsetMultipler ensures it can only pan to the edge of the image, no further
        let finalOffsetX = 0;
        let finalOffsetY = 0;

        if (doPanX) {
          const offsetMultiplierX = ((this.currentZoomImage.clientWidth - this.zoomContainer.clientWidth) / 2) / midX;
          finalOffsetX = Math.round(-offsetFromCentreX * offsetMultiplierX);
        }
        if (doPanY) {
          const offsetMultiplierY = ((this.currentZoomImage.clientHeight - this.zoomContainer.clientHeight) / 2) / midY;
          finalOffsetY = Math.round(-offsetFromCentreY * offsetMultiplierY);
        }

        this.currentTransform.panX = finalOffsetX;
        this.currentTransform.panY = finalOffsetY;
        this.alterCurrentPanBy(0, 0); // sanitise
        this.updateImagePosition();
      }
    }

    /**
     * Update pan values by a delta.
     * @param {number} x - Distance to pan X.
     * @param {number} y - Distance to pan Y.
     */
    alterCurrentPanBy(x, y) {
      this.currentTransform.panX += x;
      // limit offset to keep most of image on screen
      let panXMax = (this.currentZoomImage.naturalWidth * this.currentTransform.zoom - this.zoomContainer.clientWidth) / 2.0;
      panXMax = Math.max(panXMax, 0);
      this.currentTransform.panX = Math.min(this.currentTransform.panX, panXMax);
      this.currentTransform.panX = Math.max(this.currentTransform.panX, -panXMax);

      this.currentTransform.panY += y;
      let panYMax = (this.currentZoomImage.naturalHeight * this.currentTransform.zoom - this.zoomContainer.clientHeight) / 2.0;
      panYMax = Math.max(panYMax, 0);
      this.currentTransform.panY = Math.min(this.currentTransform.panY, panYMax);
      this.currentTransform.panY = Math.max(this.currentTransform.panY, -panYMax);
      this.updateImagePosition();
    }

    /**
     * Set current zoom image transform to specific values.
     * @param {number} panX - Pan X value.
     * @param {number} panY - Pan Y value.
     * @param {number} zoom - Current zoom amount.
     */
    setCurrentTransform(panX, panY, zoom) {
      this.currentTransform.panX = panX;
      this.currentTransform.panY = panY;
      this.currentTransform.zoom = zoom;
      this.alterCurrentTransformZoomBy(0);
    }

    /**
     * Update zoom amount by a delta.
     * @param {number} delta - Amount to adjust.
     */
    alterCurrentTransformZoomBy(delta) {
      this.currentTransform.zoom += delta;
      // do not zoom out further than fit
      const maxZoomX = this.zoomContainer.clientWidth / this.currentZoomImage.naturalWidth;
      const maxZoomY = this.zoomContainer.clientHeight / this.currentZoomImage.naturalHeight;
      this.currentTransform.zoom = Math.max(this.currentTransform.zoom, Math.min(maxZoomX, maxZoomY));

      // do not zoom in further than native size
      this.currentTransform.zoom = Math.min(this.currentTransform.zoom, 1.0);

      // reasses pan bounds
      this.alterCurrentPanBy(0, 0);
      this.updateImagePosition();
    }

    /**
     * Position the current image in the centre, zoomed out
     */
    setInitialImagePosition() {
      this.updateStripReserve();
      // The ResizeObserver fires once on observe, before init() has loaded an image.
      if (!this.currentZoomImage) return;
      this.currentZoomImage.style.top = `${this.zoomContainer.clientHeight / 2 - this.currentZoomImage.clientHeight / 2}px`;
      this.currentZoomImage.style.left = `${this.zoomContainer.clientWidth / 2 - this.currentZoomImage.clientWidth / 2}px`;
      this.setCurrentTransform(0, 0, 0);
    }

    /**
     * Shortens the zoom container by the thumbnail row's height (via
     * --gallery-zoom-strip, read by gallery-zoom.css) so the fitted image ends
     * where the row begins instead of under it — a drawing's bottom measurement
     * was hidden by the row (ClickUp 869fk4e47). offsetHeight, not a rect: the
     * modal window scales .8 → 1 while opening, and a cached image can load
     * inside that transition.
     */
    updateStripReserve() {
      const strip = this.querySelector('.gallery-zoom__carousel');
      const isShown = strip && !strip.classList.contains('visually-hidden') && !this.thumbContainer.hidden;
      this.style.setProperty('--gallery-zoom-strip', `${isShown ? strip.offsetHeight : 0}px`);
    }

    /**
     * Set current zoom image transform based on pan & zoom values.
     */
    updateImagePosition() {
      this.currentZoomImage.style.transform = `translate3d(${this.currentTransform.panX}px, ${this.currentTransform.panY}px, 0) scale(${this.currentTransform.zoom})`;
    }

    /**
     * Select a thumbnail.
     * @param {Element} thumb - Thumbnail to select.
     */
    selectThumb(thumb) {
      let activeThumb;

      [...thumb.parentElement.children].forEach((el) => {
        if (el === thumb) {
          activeThumb = thumb;
          el.classList.add('gallery-zoom__thumb--active');
        } else {
          el.classList.remove('gallery-zoom__thumb--active');
        }
      });

      const visibleThumbs = Array.from(activeThumb.parentElement.children).filter((child) => !child.hidden);
      const activeThumbIndex = visibleThumbs.indexOf(activeThumb);
      if (this.counterCurrent) this.counterCurrent.textContent = activeThumbIndex + 1;

      // If the thumb isn't visible, scroll to it
      this.scrollToThumb(activeThumb);

      this.previousBtn.disabled = activeThumbIndex === 0;
      this.nextBtn.disabled = (activeThumbIndex === (visibleThumbs.length - 1));

      // replace zoom image
      this.zoomContainer.classList.add('gallery-zoom__zoom-container--loading');
      this.currentZoomImage = GalleryZoom.createEl('img', 'gallery-zoom__zoom-image');
      this.currentZoomImage.alt = thumb.querySelector('.gallery-zoom__thumb-img')?.alt;
      this.currentZoomImage.style.visibility = 'hidden';
      this.currentZoomImage.onload = () => {
        this.zoomContainer.classList.remove('gallery-zoom__zoom-container--loading');
        this.currentZoomImage.style.visibility = '';
        this.setInitialImagePosition();
      };
      this.currentZoomImage.src = thumb.dataset.zoomUrl;
      this.zoomContainer.replaceChildren(this.currentZoomImage);
    }

    /**
     * Scrolls the thumbnail slider to the active thumbnail if necessary
     * @param {Element} thumb - Thumbnail to scroll to.
     */
    scrollToThumb(thumb) {
      const thumbSliderRect = this.thumbSlider.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();

      const thumbLeft = thumbRect.left - thumbSliderRect.left;
      const thumbRight = thumbRect.right - thumbSliderRect.right;

      if (thumbLeft < 0) {
        this.thumbSlider.scrollLeft += thumbLeft - (this.thumbSlider.clientWidth - thumbRect.width);
      } else if (thumbRight > 0) {
        this.thumbSlider.scrollLeft += thumbRight + (this.thumbSlider.clientWidth - thumbRect.width);
      }
    }

    /**
     * Select previous thumbnail.
     * @param {Event} evt - Click event, if triggered by pagination.
     */
    selectPreviousThumb(evt) {
      if (evt) evt.preventDefault();
      if (this.thumbContainer.childElementCount < 2) return;

      let previous = this.thumbContainer.querySelector('.gallery-zoom__thumb--active').previousElementSibling;
      // Bounded walk: one lap around the strip is the most this can need.
      // Every thumb can legitimately be hidden at once — a variant whose
      // asset group is still hydrating has no slide of its own on screen yet
      // — and an unbounded walk in that state spins the main thread and
      // freezes the tab (no close, no scroll, nothing). Bail to a no-op
      // instead; the next open re-derives visibility from the gallery.
      let steps = this.thumbContainer.childElementCount + 1;
      while ((!previous || previous.hidden) && steps > 0) {
        previous = previous ? previous.previousElementSibling : this.thumbContainer.lastElementChild;
        steps -= 1;
      }
      if (!previous || previous.hidden) return;
      this.selectThumb(previous);
    }

    /**
     * Select next thumbnail.
     * @param {Event} evt - Click event, if triggered by pagination.
     */
    selectNextThumb(evt) {
      if (evt) evt.preventDefault();
      if (this.thumbContainer.childElementCount < 2) return;

      let next = this.thumbContainer.querySelector('.gallery-zoom__thumb--active').nextElementSibling;
      // Bounded for the same reason as selectPreviousThumb() above.
      let steps = this.thumbContainer.childElementCount + 1;
      while ((!next || next.hidden) && steps > 0) {
        next = next ? next.nextElementSibling : this.thumbContainer.firstElementChild;
        steps -= 1;
      }
      if (!next || next.hidden) return;
      this.selectThumb(next);
    }

    /**
     * Call to stop tracking touch events.
     */
    stopTrackingTouch() {
      this.pinchTracking.isTracking = false;
      this.touchTracking.isTracking = false;
    }

    /**
     * Handle mouse and touch events.
     * @param {object} evt - Event object.
     */
    trackInputMovement(evt) {
      evt.preventDefault();
      if (evt.type === 'touchmove' && evt.touches.length > 0) {
        // pan
        const touch1 = evt.touches[0];
        if (!this.touchTracking.isTracking) {
          this.touchTracking.isTracking = true;
          this.touchTracking.lastTouchX = touch1.clientX;
          this.touchTracking.lastTouchY = touch1.clientY;
        } else {
          this.alterCurrentPanBy(
            (touch1.clientX - this.touchTracking.lastTouchX) * this.touchPanModifier,
            (touch1.clientY - this.touchTracking.lastTouchY) * this.touchPanModifier
          );
          this.touchTracking.lastTouchX = touch1.clientX;
          this.touchTracking.lastTouchY = touch1.clientY;
        }

        if (evt.touches.length === 2) {
          // pinch
          const touch2 = evt.touches[1];
          const pinchDistance = Math.sqrt((touch1.clientX - touch2.clientX) ** 2 + (touch1.clientY - touch2.clientY) ** 2);
          if (!this.pinchTracking.isTracking) {
            this.pinchTracking.lastPinchDistance = pinchDistance;
            this.pinchTracking.isTracking = true;
          } else {
            const pinchDelta = pinchDistance - this.pinchTracking.lastPinchDistance;
            this.alterCurrentTransformZoomBy(pinchDelta * this.pinchZoomMultiplier);
            this.pinchTracking.lastPinchDistance = pinchDistance;
          }
        } else {
          this.pinchTracking.isTracking = false;
        }
      } else {
        // mousemove
        this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
      }
    }

    /**
     * Handle mouse wheel scroll - use for zoom.
     * @param {object} evt - Event object.
     */
    trackWheel(evt) {
      evt.preventDefault();
      if (evt.deltaY !== 0) {
        this.alterCurrentTransformZoomBy(evt.deltaY * this.wheelZoomMultiplier);
      }
    }

    /**
     * Handle click on a thumbnail image.
     * @param {object} evt - Event object.
     */
    onThumbClick(evt) {
      evt.preventDefault();
      this.selectThumb(evt.currentTarget);
    }

    /**
     * Handle click on the main zoom image. (Toggle zoom in/out.)
     * @param {object} evt - Event object.
     */
    onZoomContainerClick(evt) {
      evt.preventDefault();

      if (this.currentTransform.zoom === 1.0) {
        this.currentTransform.zoom = 0;
        this.alterCurrentTransformZoomBy(0);
      } else {
        this.currentTransform.zoom = 1;
        this.alterCurrentTransformZoomBy(0);
        this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
      }
    }

    /**
     * Handle key events.
     * @param {object} evt - Event object.
     */
    handleKeyup(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
          evt.preventDefault();
          this.selectPreviousThumb();
          break;
        case 'ArrowRight':
          evt.preventDefault();
          this.selectNextThumb();
          break;
      }
    }
  }

  window.customElements.define('gallery-zoom', GalleryZoom);
}
